import {
  AfterContentInit,
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  inject,
  Input,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { CdkDrag, CdkDragEnd, CdkDragHandle, CdkDragStart } from "@angular/cdk/drag-drop";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { Constants } from "@config/constants";
import { SignService } from "@services/sign.service";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { FormsModule } from "@angular/forms";

@Component({
  selector: "kzn-sign-drag-resize",
  standalone: true,
  imports: [
    CommonModule,
    CdkDrag,
    CdkDragHandle,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule
  ],
  templateUrl: "./sign-drag-resize.component.html",
  styleUrl: "./sign-drag-resize.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class SignDragResizeComponent implements OnInit, AfterViewInit {
  @ContentChild('tagContent', { static: false }) tagContent!: ElementRef;
  @Input() isCustomTagElement: boolean = false;
  @Input() isCustomTagElementStatic: boolean = false;
  @Input() onRemoveElement: (() => void) | undefined;

  Constants = Constants;
  imageBase64!: string;
  signatureId: string = '';
  signComment: string | undefined;
  left!: string;
  top!: string;
  id!: string;
  isDraggable: boolean = true;
  isCommentOnly: boolean = false;
  comment: string | undefined = "";
  height: string = '';
  width: string = '';
  isResponsiveContainer?: boolean = false;
  disableDelete?: boolean = false;
  destroyComponent!: () => void;

  private _idPrefix: string = "sign-drag-resize-";

  private _host = inject(ElementRef<HTMLElement>);

  constructor(public signService: SignService) { }

  onDragStarted(_event: CdkDragStart): void {
    this.signService.notifyTagDragSuppressNext(true);
  }

  onDragEnded(event: CdkDragEnd): void {
    try {
      const resizable = event.source.element.nativeElement as HTMLElement;
      const page = resizable.closest('.page') as HTMLElement | null;
      const frameEl = ((page?.querySelector('.textLayer') as HTMLElement | null) ?? page) as HTMLElement | null;
      if (!frameEl) {
        event.source.reset();
        return;
      }

      const dragRect = resizable.getBoundingClientRect();
      const frameRect = frameEl.getBoundingClientRect();
      const refWidth = frameEl.clientWidth || frameRect.width || 1;
      const refHeight = frameEl.clientHeight || frameRect.height || 1;
      const leftPercent = ((dragRect.left - frameRect.left) / refWidth) * 100;
      const topPercent = ((dragRect.top - frameRect.top) / refHeight) * 100;

      const span = this._host.nativeElement.closest(
        '.box-sign-here, .sign-tag-element-container, .sign-tag-comment-container'
      ) as HTMLElement | null;

      const usesSpan = (this.isResponsiveContainer || this.isCustomTagElement) && !!span;
      const positioned = usesSpan ? (span as HTMLElement) : resizable;

      event.source.reset();

      positioned.style.left = `${leftPercent}%`;
      positioned.style.top = `${topPercent}%`;
      if (!usesSpan) {
        this.left = `${leftPercent}%`;
        this.top = `${topPercent}%`;
      }

      const tagId = span?.getAttribute('data-kzn-tag-id') || '';
      if (tagId) {
        this.signService.updatePlacedSignatureGeometry(tagId, span);
        this.signService.refreshPlacedSignatureSnapshot(tagId);
      }
    } catch {
      event.source.reset();
    } finally {
      this.signService.notifyTagDragSuppressNext(false, 180);
    }
  }

  selectSelf(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.action-handles-container')) {
      event.stopPropagation();
    }
    const current = this._host.nativeElement;
    document.querySelectorAll('.resizable.selected').forEach((el) => {
      if (el !== current) el.classList.remove('selected');
    });
    current.classList.add('selected');
  }

  ngOnInit(): void {
    if (this.isResponsiveContainer) {
      this.width = '100%';
      this.height = '100%';
    } else {
      const commentBoxDefaultSize = this.getCommentBoxDefaultSize();

      this.height = this.isCommentOnly
        ? commentBoxDefaultSize.height + "px"
        : (
          this.isDraggable
            ? Constants.DEFAULT_SIGN_IMAGE_HEIGHT
            : "100%"
        );
      this.width = this.isCommentOnly
        ? commentBoxDefaultSize.width + "px"
        : (
          this.isDraggable
            ? Constants.DEFAULT_SIGN_IMAGE_WIDTH
            : "100%"
        );
    }

    if (this.isCommentOnly) {
      this.isDraggable = true;
    }
    this.id = this._idPrefix + this.signService.generateUUID();
  }

  private getCommentBoxDefaultSize() {
    let height = Constants.DEFAULT_SIGN_COMMENT_HEIGHT;
    let width = Constants.DEFAULT_SIGN_COMMENT_WIDTH;

    const textLayer = document.getElementsByClassName("textLayer");
    if (textLayer.length) {
      const textLayerHeight = textLayer[0].clientHeight;
      const textLayerWidth = textLayer[0].clientWidth;

      const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
      const heightPercent = isMobile ? 8 : Constants.DEFAULT_SIGN_COMMENT_HEIGHT_PERCENTAGE;
      const widthPercent = isMobile ? 55 : Constants.DEFAULT_SIGN_COMMENT_WIDTH_PERCENTAGE;

      height = (heightPercent / 100) * textLayerHeight;
      width = (widthPercent / 100) * textLayerWidth;
    }

    return {
      height,
      width,
    };
  }

  ngAfterViewInit(): void {
    const minWidth = this.isCustomTagElement
      ? Constants.CANONICAL_TAG_LABELED_FIELD_MIN_WIDTH_PX
      : this.isCommentOnly
        ? Constants.MINIMUM_SIGN_COMMENT_WIDTH
        : Constants.MINIMUM_SIGN_IMAGE_WIDTH;
    const minHeight = this.isCustomTagElement
      ? Constants.CANONICAL_TAG_FIELD_HEIGHT_PX - 8
      : this.isCommentOnly
        ? Constants.MINIMUM_SIGN_COMMENT_HEIGHT
        : Constants.MINIMUM_SIGN_IMAGE_HEIGHT;

    this.signService.makeResizableElement(`#${this.id}`, minHeight, minWidth);
  }
}
